/**
 * installState — pure CAPABILITY detection for the installed-app lane.
 *
 * There is no native app: the "mobile application" IS this site installed as
 * a PWA (Add to Home Screen on iOS Safari, the install prompt on Chromium
 * Android). This module answers only FACTS about the browser we are running
 * in — never a product decision (master rule; D8: no keyword/classifier
 * shortcut decides behaviour; D9: code authors no content). Whether an offer
 * is made, when, and in what words is the banner component's business; the
 * facts it needs are:
 *
 *   - isStandalone()             running as the installed app right now?
 *   - getMobilePlatform()        'ios' | 'android' | 'other' (iPadOS reports a
 *                                Mac UA — Macintosh + a touch screen is iOS)
 *   - isIosSafari()              real Safari on iOS — the only iOS browser
 *                                with "Add to Home Screen" (Chrome/Firefox/
 *                                Edge on iOS and in-app webviews cannot)
 *   - the deferred `beforeinstallprompt` event a Chromium browser hands the
 *     page (captured at MODULE LOAD — the event fires once, early, and is
 *     lost if nobody is listening; preventDefault keeps the browser's own
 *     mini-infobar from firing so the install is offered from OUR click)
 *   - `appinstalled` — the browser's own "it is installed now" signal
 *   - display-mode changes (installed app opened / window mode changed)
 *   - navigator.getInstalledRelatedApps — "already installed" when the
 *     platform can tell us (needs manifest.related_applications, webapp)
 *
 * Every browser API call is wrapped in try/catch: SSR, jsdom, and locked-down
 * browsers must read as "not standalone / other / no prompt", never throw.
 */

/** The Chromium install-prompt event (not in lib.dom). */
export interface BeforeInstallPromptEvent extends Event {
  readonly platforms?: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

export type MobilePlatform = 'ios' | 'android' | 'other';

export type InstallStateEvent = 'prompt-captured' | 'app-installed' | 'display-mode';

/**
 * The chat's mobile breakpoint, verbatim (useGlobalChat.ts viewport detection,
 * GlobalFooter, the sidebar's own media block): below it the chat wears its
 * phone layout, so "on a phone" and "chat is in phone mode" agree.
 */
export const MOBILE_VIEWPORT_QUERY = '(max-width: 820px)';

/** Display modes that mean "running as the installed app" (manifest display / display_override). */
export const INSTALLED_DISPLAY_MODES = ['standalone', 'minimal-ui', 'fullscreen'] as const;

// ── Module state ─────────────────────────────────────────────────────────────

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let appInstalledSeen = false;
const subscribers = new Set<(ev: InstallStateEvent) => void>();

function notify(ev: InstallStateEvent): void {
  subscribers.forEach((cb) => {
    try {
      cb(ev);
    } catch {
      /* a subscriber's error must never break the others */
    }
  });
}

function safeMatchMedia(query: string): MediaQueryList | null {
  try {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
    return window.matchMedia(query) ?? null;
  } catch {
    return null;
  }
}

function userAgent(): string {
  try {
    return typeof navigator !== 'undefined' ? String(navigator.userAgent || '') : '';
  } catch {
    return '';
  }
}

function maxTouchPoints(): number {
  try {
    const n = typeof navigator !== 'undefined' ? Number(navigator.maxTouchPoints) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

// ── Facts ────────────────────────────────────────────────────────────────────

/** Running as the installed app right now (display-mode media or Safari's navigator.standalone). */
export function isStandalone(): boolean {
  try {
    for (const mode of INSTALLED_DISPLAY_MODES) {
      if (safeMatchMedia(`(display-mode: ${mode})`)?.matches) return true;
    }
  } catch {
    /* fall through to the Safari flag */
  }
  try {
    const nav = typeof navigator !== 'undefined'
      ? (navigator as Navigator & { standalone?: boolean })
      : null;
    if (nav && nav.standalone === true) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Platform family from the UA. iPadOS 13+ Safari reports a desktop Mac UA by
 * default; a "Macintosh" with a multi-touch screen is an iPad.
 */
export function getMobilePlatform(ua: string = userAgent(), touchPoints: number = maxTouchPoints()): MobilePlatform {
  try {
    if (/\b(iPad|iPhone|iPod)\b/i.test(ua)) return 'ios';
    if (/Macintosh/i.test(ua) && touchPoints > 1) return 'ios';
    if (/\bAndroid\b/i.test(ua)) return 'android';
    return 'other';
  } catch {
    return 'other';
  }
}

/**
 * Browsers on iOS that are NOT Safari (all WebKit, none can Add to Home
 * Screen): Chrome (CriOS), Firefox (FxiOS), Edge (EdgiOS), Opera (OPiOS /
 * OPT), DuckDuckGo, Brave; and in-app webviews (Facebook FBAN/FBAV/FB_IAB,
 * Instagram, LINE, Twitter, Google app GSA, Snapchat, LinkedIn).
 */
const NON_SAFARI_IOS_RE =
  /(CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|DuckDuckGo|Brave|FBAN|FBAV|FB_IAB|Instagram|\bLine\/|Twitter|GSA\/|Snapchat|LinkedInApp)/i;

/** Real Safari on iOS/iPadOS — the only iOS browser with "Add to Home Screen". */
export function isIosSafari(ua: string = userAgent(), touchPoints: number = maxTouchPoints()): boolean {
  try {
    if (getMobilePlatform(ua, touchPoints) !== 'ios') return false;
    if (NON_SAFARI_IOS_RE.test(ua)) return false;
    // Safari carries both tokens; a bare WKWebView (in-app browser) lacks "Safari/".
    return /Safari\//.test(ua) && /Version\//.test(ua);
  } catch {
    return false;
  }
}

/** The chat's phone breakpoint, as a fact. Unknown (SSR/jsdom without matchMedia) reads false. */
export function isMobileViewport(): boolean {
  return safeMatchMedia(MOBILE_VIEWPORT_QUERY)?.matches === true;
}

// ── Deferred install prompt (Chromium) ───────────────────────────────────────

/** The stashed beforeinstallprompt event, if the browser offered one and nobody has used it. */
export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

/**
 * Take the stashed event (it is single-use: once prompt() has been called on
 * it the browser will not honour a second call). Returns null when none is
 * available — the caller must treat that as "nothing to offer", never as an
 * error to show.
 */
export function consumeDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  const ev = deferredPrompt;
  deferredPrompt = null;
  return ev;
}

/** Did the browser fire `appinstalled` during this page's life? */
export function wasAppInstalledThisPage(): boolean {
  return appInstalledSeen;
}

/**
 * Ask the platform whether this web app is already installed
 * (navigator.getInstalledRelatedApps; Chromium Android, needs
 * manifest.related_applications to include the webapp platform). Anything
 * unavailable, rejected, or odd resolves false: "unknown" must never read as
 * "installed" and hide the offer, nor throw and break the banner.
 */
export async function isInstalledRelatedApp(): Promise<boolean> {
  try {
    const nav = typeof navigator !== 'undefined'
      ? (navigator as Navigator & { getInstalledRelatedApps?: () => Promise<unknown[]> })
      : null;
    if (!nav || typeof nav.getInstalledRelatedApps !== 'function') return false;
    const apps = await nav.getInstalledRelatedApps();
    return Array.isArray(apps) && apps.length > 0;
  } catch {
    return false;
  }
}

// ── Subscriptions ────────────────────────────────────────────────────────────

/** Fires on prompt capture, appinstalled, and display-mode change. Returns the unsubscribe. */
export function subscribeInstallState(cb: (ev: InstallStateEvent) => void): () => void {
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
  };
}

/**
 * Track the installed display modes; `cb` receives the CURRENT standalone
 * answer on every change. Safe where matchMedia is missing (no-op unsubscribe).
 */
export function subscribeDisplayMode(cb: (standalone: boolean) => void): () => void {
  const lists: MediaQueryList[] = [];
  for (const mode of INSTALLED_DISPLAY_MODES) {
    const mq = safeMatchMedia(`(display-mode: ${mode})`);
    if (mq) lists.push(mq);
  }
  if (lists.length === 0) return () => {};
  const handler = () => {
    try {
      cb(isStandalone());
    } catch {
      /* ignore */
    }
    notify('display-mode');
  };
  const detachers: Array<() => void> = [];
  for (const mq of lists) {
    try {
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', handler);
        detachers.push(() => mq.removeEventListener('change', handler));
      } else if (typeof (mq as MediaQueryList & { addListener?: (h: () => void) => void }).addListener === 'function') {
        // Safari < 14 shape.
        (mq as MediaQueryList & { addListener: (h: () => void) => void }).addListener(handler);
        detachers.push(() =>
          (mq as MediaQueryList & { removeListener: (h: () => void) => void }).removeListener(handler),
        );
      }
    } catch {
      /* ignore this list */
    }
  }
  return () => detachers.forEach((d) => {
    try { d(); } catch { /* ignore */ }
  });
}

/** Track the chat's phone breakpoint; `cb` receives the current answer on change. */
export function subscribeMobileViewport(cb: (mobile: boolean) => void): () => void {
  const mq = safeMatchMedia(MOBILE_VIEWPORT_QUERY);
  if (!mq) return () => {};
  const handler = () => {
    try {
      cb(mq.matches === true);
    } catch {
      /* ignore */
    }
  };
  try {
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    const legacy = mq as MediaQueryList & {
      addListener?: (h: () => void) => void;
      removeListener?: (h: () => void) => void;
    };
    if (typeof legacy.addListener === 'function') {
      legacy.addListener(handler);
      return () => legacy.removeListener?.(handler);
    }
  } catch {
    /* ignore */
  }
  return () => {};
}

// ── Module-load capture ──────────────────────────────────────────────────────

function onBeforeInstallPrompt(e: Event): void {
  try {
    // Keep Chromium's own mini-infobar quiet: the install is offered from our
    // click, on our banner, when the primary surface says it may speak.
    e.preventDefault();
  } catch {
    /* ignore */
  }
  deferredPrompt = e as BeforeInstallPromptEvent;
  notify('prompt-captured');
}

function onAppInstalled(): void {
  appInstalledSeen = true;
  deferredPrompt = null;
  notify('app-installed');
}

if (typeof window !== 'undefined') {
  try {
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
  } catch {
    /* no listeners — the facts above still answer honestly */
  }
}

/** Test-only: clear the stash and the installed flag between cases. */
export function __resetInstallStateForTests(): void {
  deferredPrompt = null;
  appInstalledSeen = false;
  subscribers.clear();
}
